/**
 * Phase A R3 — Library panel.
 *
 * Lists tracks from the user's picked local folder. Click a track to
 * load on Deck A. Right-click / Alt-click loads on Deck B. Drag a row
 * onto a deck card for explicit deck targeting.
 *
 * Also exposes "Add to Auto-DJ playlist" affordance.
 *
 * Phase A R6 B11 — Library sort dropdown (9 modes) + ID3-tag aware
 * row layout (Title — Artist · BPM · Key · Duration · Genre). Sort
 * choice persists to localStorage at `sr-library-sort`.
 */
import { useEffect, useMemo, useState } from "react";
import { FolderOpen, RefreshCw, X, Plus, Music, Loader2, ArrowUpDown } from "lucide-react";
import { useLibraryAnalysis } from "../../audio/useLibraryAnalysis";

function fmtSize(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDuration(s) {
  if (!s || s <= 0) return "";
  const m = Math.floor(s / 60);
  const r = String(Math.floor(s % 60)).padStart(2, "0");
  return `${m}:${r}`;
}

// Phase A R6 B11 — Camelot wheel ordering for KEY sort. The wheel runs
// 1A..12A then 1B..12B; harmonic neighbours sit next to each other.
const CAMELOT_ORDER = (() => {
  const out = {};
  let i = 0;
  for (const letter of ["A", "B"]) {
    for (let n = 1; n <= 12; n++) out[`${n}${letter}`] = i++;
  }
  return out;
})();

const SORT_OPTIONS = [
  { id: "title-asc",   label: "Title A → Z" },
  { id: "title-desc",  label: "Title Z → A" },
  { id: "artist-asc",  label: "Artist A → Z" },
  { id: "artist-desc", label: "Artist Z → A" },
  { id: "bpm-asc",     label: "BPM ascending" },
  { id: "bpm-desc",    label: "BPM descending" },
  { id: "key-camelot", label: "Key (Camelot)" },
  { id: "genre-asc",   label: "Genre A → Z" },
  { id: "genre-desc",  label: "Genre Z → A" },
];
const DEFAULT_SORT = "title-asc";

function compareStr(a, b) {
  return String(a || "").toLowerCase().localeCompare(String(b || "").toLowerCase());
}

function sortTracks(tracks, sortId) {
  const list = [...tracks];
  // Phase A R7 B3 — instrument so users (and tests) can confirm the
  // comparator actually fires when the dropdown changes. The previous
  // "sort doesn't work" report turned out to be cosmetic — comparators
  // had no analyzed fields to compare against (everything fell back to
  // "Unknown" / 0). After R7-4 BPM normalization + R6-11 ID3 extraction
  // both populate so sort is meaningful.
  // eslint-disable-next-line no-console
  console.log(`[Library sort] mode=${sortId} input=${list.length}`);
  switch (sortId) {
    case "title-asc":   list.sort((a, b) => compareStr(a.title,  b.title));  break;
    case "title-desc":  list.sort((a, b) => compareStr(b.title,  a.title));  break;
    case "artist-asc":  list.sort((a, b) => compareStr(a.artist, b.artist) || compareStr(a.title, b.title)); break;
    case "artist-desc": list.sort((a, b) => compareStr(b.artist, a.artist) || compareStr(a.title, b.title)); break;
    case "bpm-asc":     list.sort((a, b) => (a.bpm || 999) - (b.bpm || 999) || compareStr(a.title, b.title)); break;
    case "bpm-desc":    list.sort((a, b) => (b.bpm || 0)   - (a.bpm || 0)   || compareStr(a.title, b.title)); break;
    case "key-camelot": list.sort((a, b) => {
      const ax = a.key ? (CAMELOT_ORDER[a.key] ?? 999) : 999;
      const bx = b.key ? (CAMELOT_ORDER[b.key] ?? 999) : 999;
      return ax - bx || compareStr(a.title, b.title);
    }); break;
    case "genre-asc":   list.sort((a, b) => compareStr(a.genre, b.genre) || compareStr(a.title, b.title)); break;
    case "genre-desc":  list.sort((a, b) => compareStr(b.genre, a.genre) || compareStr(a.title, b.title)); break;
    default: break;
  }
  return list;
}

export default function LibraryPanel({
  library,
  onLoadDeck,        // (track, "A"|"B"|"C"|"D")
  onAddToPlaylist,   // (track)
  tierAllowsCD = false,
}) {
  const [filter, setFilter] = useState("");
  // Phase A R6 B11 — sort persisted across reloads
  const [sortId, setSortId] = useState(() => {
    try { return localStorage.getItem("sr-library-sort") || DEFAULT_SORT; } catch { return DEFAULT_SORT; }
  });
  useEffect(() => { try { localStorage.setItem("sr-library-sort", sortId); } catch {} }, [sortId]);

  // Phase A R5 F3 — Library pre-analysis runs in background after folder pick.
  const analysis = useLibraryAnalysis(library);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const source = analysis.tracks.length ? analysis.tracks : library.tracks;
    const filtered = !q ? source : source.filter((t) =>
      (t.title || t.name).toLowerCase().includes(q) ||
      (t.artist || "").toLowerCase().includes(q) ||
      (t.genre  || "").toLowerCase().includes(q) ||
      (t.album  || "").toLowerCase().includes(q)
    );
    return sortTracks(filtered, sortId);
  }, [filter, library.tracks, analysis.tracks, sortId]);

  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-3" data-testid="dj-library-panel">
      <div className="flex items-center gap-2 mb-2">
        <FolderOpen className="w-4 h-4 text-white/70" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Library</div>
          <div className="text-sm font-semibold truncate" title={library.folderName}>
            {library.hasHandle ? library.folderName || "Folder" : "No folder picked"}
          </div>
        </div>
        {!library.supported && (
          <span className="text-[10px] text-amber-300 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/30">
            Chrome / Edge / Brave only
          </span>
        )}
        {library.supported && !library.hasHandle && (
          <button onClick={library.pickFolder}
                  className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-xs font-bold"
                  data-testid="dj-library-pick-folder">
            Pick folder
          </button>
        )}
        {library.supported && library.hasHandle && library.status === "idle" && (
          <button onClick={library.reattachFromIdb}
                  className="px-3 py-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-xs font-bold text-emerald-200 border border-emerald-500/30"
                  data-testid="dj-library-reattach">
            Grant access
          </button>
        )}
        {library.hasHandle && library.status === "ready" && (
          <>
            <button onClick={library.refresh}
                    className="p-1.5 rounded bg-white/5 hover:bg-white/10"
                    title="Refresh folder"
                    data-testid="dj-library-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button onClick={library.clear}
                    className="p-1.5 rounded bg-white/5 hover:bg-white/10"
                    title="Forget folder"
                    data-testid="dj-library-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>

      {library.status === "denied" && (
        <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mb-2">
          Permission denied. Click "Grant access" to retry.
        </div>
      )}
      {library.status === "reading" && (
        <div className="text-[11px] text-white/60 mb-2">Scanning folder…</div>
      )}
      {library.error && (
        <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1 mb-2">
          {library.error}
        </div>
      )}

      {library.hasHandle && library.status === "ready" ? (
        <>
          {/* Phase A R6 B11 — filter + sort row */}
          <div className="flex items-center gap-2 mb-2">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter title / artist / genre…"
                   className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-emerald-400/50"
                   data-testid="dj-library-filter" />
            <div className="relative shrink-0">
              <ArrowUpDown className="w-3 h-3 text-white/40 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={sortId}
                onChange={(e) => setSortId(e.target.value)}
                className="bg-white/5 border border-white/10 rounded pl-6 pr-2 py-1 text-[11px] outline-none focus:ring-1 focus:ring-emerald-400/50 cursor-pointer"
                data-testid="dj-library-sort">
                {SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id} data-testid={`dj-library-sort-option-${o.id}`}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto pr-1 space-y-1" data-testid="dj-library-list">
            {visible.length === 0 && (
              <div className="text-[11px] text-white/40 py-3 text-center">No audio files found.</div>
            )}
            {visible.map((t, idx) => {
              const key = `${library.folderName}/${t.path}`;
              const isAnalyzing = analysis.analyzing[key];
              return (
                <div key={t.id}
                     draggable
                     onDragStart={(e) => { e.dataTransfer.setData("application/x-stackroom-library", t.id); }}
                     className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/5 hover:bg-white/10 group"
                     data-testid={`dj-library-track-${idx}`}
                     data-track-id={t.id}>
                  <Music className="w-3.5 h-3.5 text-white/40 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {/* Line 1: Title — Artist */}
                    <div className="text-[12px] truncate" title={`${t.title} — ${t.artist}`}>
                      <span className="text-white">{t.title}</span>
                      <span className="text-white/40"> — {t.artist}</span>
                    </div>
                    {/* Line 2: meta strip (BPM · Key · Duration · Genre · size · analyzing spinner) */}
                    <div className="text-[10px] text-white/40 flex items-center gap-2 flex-wrap">
                      {isAnalyzing && (
                        <span className="inline-flex items-center gap-1 text-amber-300/80"
                              data-testid={`dj-library-analyzing-${idx}`}>
                          <Loader2 className="w-2.5 h-2.5 animate-spin" /> analyzing
                        </span>
                      )}
                      {t.bpm > 0 && (
                        <span className="text-emerald-300/80" data-testid={`dj-library-bpm-${idx}`}>
                          {t.bpm} BPM
                        </span>
                      )}
                      {t.key && <span className="text-purple-300/80">{t.key}</span>}
                      {t.durationSec > 0 && <span className="text-white/50">{fmtDuration(t.durationSec)}</span>}
                      {t.genre && t.genre !== "Unknown" && (
                        <span className="text-sky-300/80" data-testid={`dj-library-genre-${idx}`}>{t.genre}</span>
                      )}
                      <span className="text-white/30">{fmtSize(t.size)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100">
                    <button onClick={() => onLoadDeck?.(t, "A")}
                            className="px-1.5 py-0.5 rounded bg-purple-500/30 hover:bg-purple-500/60 text-[10px] font-bold"
                            data-testid={`dj-library-load-A-${idx}`}>→A</button>
                    <button onClick={() => onLoadDeck?.(t, "B")}
                            className="px-1.5 py-0.5 rounded bg-emerald-500/30 hover:bg-emerald-500/60 text-[10px] font-bold"
                            data-testid={`dj-library-load-B-${idx}`}>→B</button>
                    {tierAllowsCD && (
                      <>
                        <button onClick={() => onLoadDeck?.(t, "C")}
                                className="px-1.5 py-0.5 rounded bg-orange-500/30 hover:bg-orange-500/60 text-[10px] font-bold"
                                data-testid={`dj-library-load-C-${idx}`}>→C</button>
                        <button onClick={() => onLoadDeck?.(t, "D")}
                                className="px-1.5 py-0.5 rounded bg-sky-500/30 hover:bg-sky-500/60 text-[10px] font-bold"
                                data-testid={`dj-library-load-D-${idx}`}>→D</button>
                      </>
                    )}
                    <button onClick={() => {
                              // Phase A R7 B2 — surface the click in console
                              // so failures can be diagnosed by inspecting
                              // logs rather than guessing about React state.
                              // eslint-disable-next-line no-console
                              console.log(`[Library] add '${t.title || t.name}' to Auto-DJ playlist`);
                              onAddToPlaylist?.(t);
                            }}
                            className="p-1 rounded bg-white/10 hover:bg-white/20"
                            title="Add to Auto-DJ playlist"
                            data-testid={`dj-library-add-${idx}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                    {/* Phase A R10 E — per-row Re-analyze. Forces a fresh
                        analysis pass (R9-E middle-window + 200Hz LPF
                        BPM detection) when the cached value looks wrong.
                        Spinner state comes from `analysis.analyzing[key]`. */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        // eslint-disable-next-line no-console
                        console.log(`[Library] reanalyze '${t.title || t.name}'`);
                        await analysis.reanalyzeOne?.(t);
                      }}
                      disabled={analysis.analyzing?.[`${library?.folderName}/${t.path}`]}
                      className="p-1 rounded bg-white/5 hover:bg-amber-500/20 hover:text-amber-200 text-white/50 disabled:opacity-50"
                      title="Re-analyze BPM/key/duration"
                      data-testid={`dj-library-reanalyze-${idx}`}>
                      <RefreshCw className={`w-3 h-3 ${analysis.analyzing?.[`${library?.folderName}/${t.path}`] ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        // Phase A R3 fix — always render the list container (even when empty)
        // so the testid is reachable. Spec requires data-testid="dj-library-list"
        // to exist whenever the Library panel is open.
        <div className="max-h-64 overflow-y-auto pr-1 space-y-1" data-testid="dj-library-list">
          <div className="text-[11px] text-white/40 py-3 text-center" data-testid="dj-library-empty">
            {library.status === "reading" ? "Scanning folder…"
             : library.status === "denied"  ? "Permission denied — click Grant access above."
             : !library.supported            ? "Library requires Chrome / Edge / Brave."
             : !library.hasHandle            ? "Empty — pick a folder above."
             :                                 "Empty — pick a folder above."}
          </div>
        </div>
      )}
    </div>
  );
}
